import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, UserCheck, Building2, ShieldCheck, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useHospitals } from '@/features/hospitals';
import {
  useApprovalConfigs,
  useCreateApprovalConfig,
  useDeleteApprovalConfig,
} from '../hooks';
import type { ApprovalConfig } from '../types';
import { localizedName } from '@/utils/localized';
import toast from 'react-hot-toast';
import {
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';

export function ApprovalConfigPage() {
  const { t, i18n } = useTranslation();
  const { data: configs, isLoading } = useApprovalConfigs();
  const { data: hospitalsData } = useHospitals();
  const hospitals = hospitalsData?.hospitals ?? [];
  const createConfig = useCreateApprovalConfig();
  const deleteConfig = useDeleteApprovalConfig();

  const [showForm, setShowForm] = useState(false);
  const [configType, setConfigType] = useState<'employee' | 'hospital'>('employee');
  const [therapistEmail, setTherapistEmail] = useState('');
  const [hospitalId, setHospitalId] = useState('');

  const handleCreate = async () => {
    try {
      await createConfig.mutateAsync({
        therapistId: configType === 'employee' ? therapistEmail : null,
        hospitalId: configType === 'hospital' ? hospitalId : null,
        requiresApproval: true,
      });
      setShowForm(false);
      setTherapistEmail('');
      setHospitalId('');
      toast.success(t('approval.configCreated'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (config: ApprovalConfig) => {
    try {
      await deleteConfig.mutateAsync(config.id);
      toast.success(t('approval.configDeleted'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Header with icon badge */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface"
        >
          <ShieldCheck className="h-6 w-6 text-slate-800" />
        </div>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">{t('approval.configTitle')}</h1>
            <p className="mt-1 text-sm text-neutral-600">{t('approval.configDescription')}</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px active:shadow-sm"
          >
            <Plus className="h-4 w-4" />
            {t('approval.addRule')}
          </button>
        </div>
      </motion.div>

      {/* Info banner */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl border border-gray-200 p-5 shadow-sm glass-surface"
      >
        <div className="flex gap-3">
          <div className="shrink-0 rounded-lg bg-white p-1.5 border border-gray-200">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-800">{t('approval.defaultBehavior')}</p>
            <p className="mt-1 text-xs font-medium text-neutral-600">
              {t('approval.defaultDescription')}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Add form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            variants={staggerItem}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -10 }}
            className="rounded-3xl overflow-hidden border border-gray-200 shadow-sm glass-surface"
          >
            <div className="px-6 py-2.5 border-b border-gray-200">
              <h3 className="font-semibold text-xs sm:text-sm">{t('approval.newRule')}</h3>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Config type selector tabs */}
              <div className="flex gap-3">
                <button
                  onClick={() => setConfigType('employee')}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition-all ${
                    configType === 'employee'
                      ? 'border-gray-200 bg-white text-primary-700 shadow-sm'
                      : 'border-gray-200 bg-white/50 text-neutral-600 hover:bg-white'
                  }`}
                >
                  <UserCheck className="h-4 w-4" />
                  {t('approval.perEmployee')}
                </button>
                <button
                  onClick={() => setConfigType('hospital')}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition-all ${
                    configType === 'hospital'
                      ? 'border-gray-200 bg-white text-primary-700 shadow-sm'
                      : 'border-gray-200 bg-white/50 text-neutral-600 hover:bg-white'
                  }`}
                >
                  <Building2 className="h-4 w-4" />
                  {t('approval.perHospital')}
                </button>
              </div>

              {/* Input field */}
              {configType === 'employee' ? (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                    {t('approval.therapistId')}
                  </label>
                  <input
                    type="text"
                    value={therapistEmail}
                    onChange={(e) => setTherapistEmail(e.target.value)}
                    placeholder={t('approval.employeeIdPlaceholder')}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm transition-all focus:translate-y-px focus:shadow-sm focus:outline-none"
                  />
                </div>
              ) : (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                    {t('rates.hospital')}
                  </label>
                  <select
                    value={hospitalId}
                    onChange={(e) => setHospitalId(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm transition-all focus:translate-y-px focus:shadow-sm focus:outline-none"
                  >
                    <option value="">{t('rates.selectHospital')}</option>
                    {hospitals.map((h) => (
                      <option key={h.id} value={h.id}>
                        {localizedName(h, i18n.language)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowForm(false);
                    setTherapistEmail('');
                    setHospitalId('');
                  }}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-neutral-600 shadow-sm transition-all hover:bg-neutral-50 active:translate-y-px active:shadow-sm"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleCreate}
                  disabled={
                    createConfig.isPending ||
                    (configType === 'employee' ? !therapistEmail : !hospitalId)
                  }
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-primary-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px active:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createConfig.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {t('common.save')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Configs list */}
      {isLoading ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring' as const, stiffness: 350, damping: 30 }}
            className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </motion.div>
        </div>
      ) : !configs || configs.length === 0 ? (
        <motion.div
          variants={staggerItem}
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-200 py-16 shadow-sm glass-surface"
        >
          <ShieldCheck className="h-12 w-12 text-green-400" />
          <p className="text-sm font-bold text-neutral-700">{t('approval.noRules')}</p>
          <p className="text-xs text-neutral-500">{t('approval.noRulesDescription')}</p>
        </motion.div>
      ) : (
        <motion.div variants={staggerItem} className="space-y-3">
          {configs.map((config, idx) => (
            <motion.div
              key={config.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                type: 'spring' as const,
                stiffness: 350,
                damping: 30,
                delay: idx * 0.05,
              }}
              className="flex items-center justify-between rounded-xl border border-gray-200 p-4 shadow-sm transition-all hover:shadow-sm glass-surface"
            >
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 shadow-sm"
                  style={{
                    backgroundColor: config.therapistId ? '#fed7aa' : '#99f6e4',
                  }}
                >
                  {config.therapistId ? (
                    <UserCheck className="h-5 w-5 text-orange-700" />
                  ) : (
                    <Building2 className="h-5 w-5 text-teal-700" />
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-neutral-800">
                    {config.therapist
                      ? `${config.therapist.firstName || ''} ${config.therapist.lastName || ''} (${config.therapist.email})`
                      : config.hospital
                        ? localizedName(config.hospital, i18n.language)
                        : t('approval.unknownTarget')}
                  </p>
                  <p className="text-xs font-medium text-neutral-500">
                    {config.therapistId ? t('approval.perEmployee') : t('approval.perHospital')}
                    {' · '}
                    {t('approval.requiresApproval')}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  if (confirm(t('approval.confirmDelete'))) {
                    handleDelete(config);
                  }
                }}
                className="rounded-lg border border-transparent p-2 text-neutral-400 transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-500 active:scale-95"
                title={t('common.delete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}
